import { useEffect, useRef, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useAppSelector } from '../../redux/store';
import { getToken } from '../../Services/auth';
import PanelLoading from '../Loading/PanelLoading';

/**
 * Ponte entre o ERP (React) e a dashboard em SolidJS.
 *
 * A dashboard é um micro-frontend: um bundle separado, gerado a partir de
 * `dashboard-solid/` e servido em `/dashboard-solid/erp-dashboard.js`, que
 * registra o Web Component <erp-dashboard>. Este componente só carrega o
 * bundle e entrega a configuração ao elemento; todo o resto (dados, gráficos,
 * tempo real) acontece dentro dele.
 *
 * O bundle pode vir de outro host definindo REACT_APP_DASHBOARD_BUNDLE_URL.
 */

const BUNDLE_URL =
  process.env.REACT_APP_DASHBOARD_BUNDLE_URL ||
  `${process.env.PUBLIC_URL || ''}/dashboard-solid/erp-dashboard.js`;

/** Selo na tela indicando a dashboard em SolidJS: só em desenvolvimento. */
const SHOW_BADGE = process.env.NODE_ENV === 'development';

let bundle: Promise<unknown> | null = null;

function loadBundle() {
  // `webpackIgnore`: o arquivo é carregado pelo navegador em tempo de
  // execução, não empacotado pelo CRA.
  bundle ??= import(/* webpackIgnore: true */ BUNDLE_URL).catch((error) => {
    bundle = null;
    throw error;
  });
  return bundle;
}

type DashboardElement = HTMLElement & { config?: unknown };

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'erp-dashboard': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

export function DashboardSolid({ apiUrl }: { apiUrl: string }) {
  const history = useHistory();
  const { storeActive } = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);
  const ref = useRef<DashboardElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    loadBundle()
      .then(() => active && setStatus('ready'))
      .catch(() => active && setStatus('error'));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (status !== 'ready') return;
    console.info(
      `%c SolidJS %c dashboard <erp-dashboard> ativa · serviço: ${apiUrl}`,
      'background:#2c4f7c;color:#fff;border-radius:3px;font-weight:bold',
      ''
    );
  }, [status, apiUrl]);

  // O React 18 só passa atributos (texto) para custom elements; a
  // configuração tem funções, então vai pela propriedade `config`.
  useEffect(() => {
    if (status !== 'ready' || !ref.current) return;
    ref.current.config = {
      apiUrl,
      storeId: storeActive?.id,
      getToken,
      navigate: (path: string) => history.push(path),
    };
  }, [status, apiUrl, storeActive?.id, history]);

  if (status === 'error') {
    return (
      <div className="alert alert-warning m-3">
        Não foi possível carregar a dashboard. Recarregue a página; se continuar,
        avise o suporte.
      </div>
    );
  }

  return (
    <>
      {SHOW_BADGE && (
        <div className="d-flex justify-content-end px-3 pt-2">
          <Badge bg="primary" title={`Serviço: ${apiUrl}`}>
            Dashboard SolidJS · {apiUrl}
          </Badge>
        </div>
      )}
      {status === 'loading' && <PanelLoading height={320} />}
      <erp-dashboard ref={ref} />
    </>
  );
}
